import React from 'react';
import addDays from 'date-fns/addDays';
import addMonths from 'date-fns/addMonths';
import format from 'date-fns/format';
import { ptBR } from 'date-fns/locale';
import DatePicker from 'react-datepicker';
import NumberFormat from 'react-number-format';
import AddLinkIcon from '@mui/icons-material/AddLink';
import ClearIcon from '@mui/icons-material/Clear';
import PaymentsIcon from '@mui/icons-material/Payments';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import LoadingButton from '@mui/lab/LoadingButton';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';

import { formatPhoneNumber } from '../utils/paymentLinks.utils';

import 'react-datepicker/dist/react-datepicker.css';

// react-number-format v4 tipa props nativas de <input>; o alias permite repassar
// as props do TextField (MUI) sem conflito de tipos.
const CurrencyInput = NumberFormat as unknown as React.ComponentType<any>;
const quickDueDateOptions = [
  { label: '1 dia', getDate: () => addDays(new Date(), 1) },
  { label: '7 dias', getDate: () => addDays(new Date(), 7) },
  { label: '15 dias', getDate: () => addDays(new Date(), 15) },
  { label: '1 mês', getDate: () => addMonths(new Date(), 1) },
];

interface CreateLinkFormProps {
  canCreateBoleto: boolean;
  form: any;
  updateForm: (field: string, value: any) => void;
  onClear: () => void;
  onSubmit: () => void;
  creating: boolean;
  destinationHint: string;
}

export const CreateLinkForm = ({
  canCreateBoleto,
  form,
  updateForm,
  onClear,
  onSubmit,
  creating,
  destinationHint,
}: CreateLinkFormProps) => {
  const [creationMode, setCreationMode] = React.useState<'simple' | 'advanced'>(
    'simple'
  );
  const selectedQuickDueDate = quickDueDateOptions.find(
    (option) => format(option.getDate(), 'yyyy-MM-dd') === form.dueDate
  )?.label;

  React.useEffect(() => {
    if (canCreateBoleto || form.type !== 'boleto') return;
    updateForm('type', 'amount');
  }, [canCreateBoleto, form.type, updateForm]);

  return (
    <Paper
      component="fieldset"
      disabled={creating}
      sx={{
        border: 0,
        minWidth: 0,
        borderRadius: 2,
        p: { xs: 2, sm: 3 },
      }}
    >
      <Box
        mb={2}
        display="flex"
        gap={1.5}
        justifyContent="space-between"
        flexWrap="wrap"
      >
        <Box>
          <Typography variant="subtitle1" fontWeight={700}>
            Dados da cobrança
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Criação de link
          </Typography>
        </Box>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={creationMode}
          onChange={(_, value) => value && setCreationMode(value)}
          sx={{
            minWidth: { xs: '100%', sm: 240 },
            '& .Mui-selected': {
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              '&:hover': { bgcolor: 'primary.dark' },
            },
          }}
        >
          <ToggleButton value="simple">Simples</ToggleButton>
          <ToggleButton value="advanced">Avançada</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Box
        display="grid"
        gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr' }}
        gap={2}
      >
        <Box gridColumn={{ sm: '1 / -1' }}>
          <ToggleButtonGroup
            exclusive
            fullWidth
            size="small"
            value={form.type}
            onChange={(_, value) => value && updateForm('type', value)}
            sx={{
              '& .Mui-selected': {
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                '&:hover': { bgcolor: 'primary.dark' },
              },
            }}
          >
            <ToggleButton value="amount" sx={{ gap: 1 }}>
              <PaymentsIcon fontSize="small" /> Valor
            </ToggleButton>
            {canCreateBoleto && (
              <ToggleButton value="boleto" sx={{ gap: 1 }}>
                <ReceiptLongIcon fontSize="small" /> Boleto
              </ToggleButton>
            )}
          </ToggleButtonGroup>
        </Box>

        <Box gridColumn={{ sm: '1 / -1' }}>
          {form.type === 'amount' ? (
            <CurrencyInput
              customInput={TextField}
              label="Valor a cobrar"
              size="small"
              prefix="R$ "
              thousandSeparator="."
              decimalSeparator=","
              decimalScale={2}
              fixedDecimalScale
              allowNegative={false}
              isNumericString
              value={form.amount}
              onValueChange={(values: any) =>
                updateForm('amount', values.value)
              }
              fullWidth
            />
          ) : (
            <TextField
              label="Código de barras"
              size="small"
              value={form.barcode}
              onChange={(event) => updateForm('barcode', event.target.value)}
              helperText="Código de barras ou linha digitável (boleto)."
              inputProps={{ maxLength: 120 }}
              fullWidth
            />
          )}
        </Box>

        <Box
          gridColumn={{ sm: '1 / -1' }}
          display="grid"
          gridTemplateColumns={{ xs: '1fr', sm: 'minmax(220px, 1fr) 1fr' }}
          gap={1.5}
          alignItems="start"
        >
          <Box
            width="100%"
            sx={{
              '.react-datepicker-wrapper': { display: 'flex', width: '100%' },
              '.react-datepicker-popper': { zIndex: 10 },
            }}
          >
            <DatePicker
              locale={ptBR}
              dateFormat="dd/MM/yyyy"
              minDate={new Date()}
              selected={
                form.dueDate ? new Date(`${form.dueDate}T00:00:00`) : null
              }
              onChange={(date) =>
                updateForm('dueDate', date ? format(date, 'yyyy-MM-dd') : '')
              }
              customInput={
                <TextField
                  label="Vencimento"
                  size="small"
                  placeholder="dd/mm/aaaa"
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
              }
            />
          </Box>

          <Box display="flex" gap={1} flexWrap="wrap">
            {quickDueDateOptions.map((option) => (
              <Button
                key={option.label}
                color="inherit"
                size="small"
                variant={
                  selectedQuickDueDate === option.label
                    ? 'contained'
                    : 'outlined'
                }
                onClick={() =>
                  updateForm('dueDate', format(option.getDate(), 'yyyy-MM-dd'))
                }
                sx={{
                  minHeight: 40,
                  px: 1.5,
                  whiteSpace: 'nowrap',
                }}
              >
                {option.label}
              </Button>
            ))}
          </Box>
        </Box>

        {creationMode === 'advanced' && (
          <>
            <Box gridColumn={{ sm: '1 / -1' }}>
              <TextField
                label="Descrição"
                size="small"
                value={form.description}
                onChange={(event) =>
                  updateForm('description', event.target.value)
                }
                placeholder="Ex.: Pedido #1234"
                inputProps={{ maxLength: 500 }}
                fullWidth
              />
            </Box>

            <TextField
              label="Referência externa"
              size="small"
              value={form.externalReference}
              onChange={(event) =>
                updateForm('externalReference', event.target.value)
              }
              helperText="Identificador do seu sistema (opcional)."
              inputProps={{ maxLength: 150 }}
              fullWidth
            />
          </>
        )}

        <Box gridColumn={{ sm: '1 / -1' }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={form.dispatch}
                onChange={(event) =>
                  updateForm('dispatch', event.target.checked)
                }
              />
            }
            label="Enviar ao cliente"
          />
        </Box>

        {form.dispatch && (
          <Box
            gridColumn={{ sm: '1 / -1' }}
            display="grid"
            gridTemplateColumns={{ xs: '1fr', sm: '120px 1fr' }}
            gap={1.5}
          >
            <FormControl size="small">
              <InputLabel>Canal</InputLabel>
              <Select
                label="Canal"
                value={form.destinationType}
                onChange={(event) =>
                  updateForm('destinationType', event.target.value)
                }
              >
                <MenuItem value="Whatsapp">WhatsApp</MenuItem>
                <MenuItem value="Email">E-mail</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Destino"
              size="small"
              placeholder={destinationHint}
              value={form.destinationValue}
              inputProps={{ maxLength: 180 }}
              onChange={(event) =>
                updateForm(
                  'destinationValue',
                  form.destinationType === 'Whatsapp'
                    ? formatPhoneNumber(event.target.value)
                    : event.target.value
                )
              }
              fullWidth
            />
          </Box>
        )}
      </Box>

      <Box
        mt={3}
        display="flex"
        justifyContent="flex-end"
        gap={1.5}
        flexWrap="wrap"
      >
        <Button color="inherit" startIcon={<ClearIcon />} onClick={onClear}>
          Limpar
        </Button>
        <LoadingButton
          variant="contained"
          loading={creating}
          onClick={onSubmit}
          startIcon={<AddLinkIcon />}
        >
          {form.dispatch ? 'Criar e enviar link' : 'Criar link'}
        </LoadingButton>
      </Box>
    </Paper>
  );
};

export default CreateLinkForm;
